import {
  customSeriesDefaultOptions,
  type CustomData,
  type CustomSeriesOptions,
  type CustomSeriesWhitespaceData,
  type ICustomSeriesPaneRenderer,
  type ICustomSeriesPaneView,
  type PaneRendererCustomData,
  type PriceToCoordinateConverter,
  type Time,
} from 'lightweight-charts';

export const NET_FUNDING_POSITIVE_COLOR = '#16a34a';
export const NET_FUNDING_NEGATIVE_COLOR = '#dc2626';
export const NET_FUNDING_ZERO_COLOR = '#94a3b8';

/** Minimum bar width in CSS pixels; keeps sparse settlement bars readable. */
const MIN_BAR_WIDTH_CSS = 10;
/** Fraction of bar spacing used once spacing exceeds the minimum width. */
const BAR_WIDTH_RATIO = 0.88;

export interface NetFundingBarData extends CustomData<Time> {
  value: number;
}

function resolveBarHalfWidthCss(barSpacing: number): number {
  const width = Math.max(MIN_BAR_WIDTH_CSS, barSpacing * BAR_WIDTH_RATIO);
  return width / 2;
}

function netFundingBarColor(value: number, color?: string): string {
  if (color) return color;
  if (value > 0) return NET_FUNDING_POSITIVE_COLOR;
  if (value < 0) return NET_FUNDING_NEGATIVE_COLOR;
  return NET_FUNDING_ZERO_COLOR;
}

export function mapNetFundingToBarData(
  points: Array<{ time: Time; value: number }>,
): NetFundingBarData[] {
  return points.map((point) => ({
    time: point.time,
    value: point.value,
    color: netFundingBarColor(point.value),
  }));
}

class NetFundingBarPaneRenderer implements ICustomSeriesPaneRenderer {
  private data: PaneRendererCustomData<Time, NetFundingBarData> | null = null;

  update(data: PaneRendererCustomData<Time, NetFundingBarData>): void {
    this.data = data;
  }

  draw(
    target: Parameters<ICustomSeriesPaneRenderer['draw']>[0],
    priceConverter: PriceToCoordinateConverter,
  ): void {
    if (!this.data?.visibleRange) return;

    target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio, verticalPixelRatio }) => {
      const baseY = priceConverter(0);
      if (baseY === null) return;

      const halfWidth = (resolveBarHalfWidthCss(this.data!.barSpacing) * horizontalPixelRatio) / 2;
      const baseLineY = Math.round(baseY * verticalPixelRatio);
      const { from, to } = this.data!.visibleRange!;

      for (let index = from; index < to; index += 1) {
        const bar = this.data!.bars[index];
        const value = bar.originalData.value;
        if (!Number.isFinite(value)) continue;

        const valueY = priceConverter(value);
        if (valueY === null) continue;

        const x = Math.round(bar.x * horizontalPixelRatio);
        const y = Math.round(valueY * verticalPixelRatio);
        const left = Math.round(x - halfWidth);
        const right = Math.round(x + halfWidth);
        const top = Math.min(y, baseLineY);
        const bottom = Math.max(y, baseLineY);

        context.fillStyle = netFundingBarColor(value, bar.originalData.color);
        context.fillRect(left, top, Math.max(1, right - left + 1), Math.max(1, bottom - top + 1));
      }
    });
  }
}

class NetFundingBarSeriesView implements ICustomSeriesPaneView<Time, NetFundingBarData> {
  private readonly rendererInstance = new NetFundingBarPaneRenderer();

  renderer(): ICustomSeriesPaneRenderer {
    return this.rendererInstance;
  }

  update(data: PaneRendererCustomData<Time, NetFundingBarData>, seriesOptions: CustomSeriesOptions): void {
    this.rendererInstance.update(data);
    void seriesOptions;
  }

  priceValueBuilder(data: NetFundingBarData): number[] {
    return [0, data.value];
  }

  isWhitespace(
    data: NetFundingBarData | CustomSeriesWhitespaceData<Time>,
  ): data is CustomSeriesWhitespaceData<Time> {
    return !('value' in data) || !Number.isFinite(data.value);
  }

  defaultOptions(): CustomSeriesOptions {
    return {
      ...customSeriesDefaultOptions,
      color: NET_FUNDING_POSITIVE_COLOR,
    };
  }
}

const netFundingBarSeriesView = new NetFundingBarSeriesView();

export function createNetFundingBarSeriesView(): ICustomSeriesPaneView<Time, NetFundingBarData> {
  return netFundingBarSeriesView;
}
